<template>
    <div>
    <v-window v-model="chartPage" vertical>
        <v-window-item :key="0">

            <v-divider />

            <v-form ref="chartForm" :lazy-validation="true" v-model="formValid">
            <v-row>
                <v-col cols="12" sm="6">
                    <div>
                        <v-tooltip bottom color="rgba(97, 97, 97, 1.0)">
                            <template v-slot:activator="{ on }">
                                <v-btn @click="showManageChartQuickloadsClick" class="mr-2" style="min-width: 40px; width: 40px;" color="#c42111" v-on="on"><v-icon>settings</v-icon></v-btn>
                            </template>
                            <span>Manage Chart Quick Loads</span>
                        </v-tooltip>
                        <v-select v-model="selectedChartType" style="display: inline-block; width: calc(100% - 48px);" ref="type" :items="chartTypes" label="Chart Type" :color="'#c42111'" :rules="[() => !!selectedChartType || 'This field is required']" />
                    </div>
                </v-col>

                <v-col cols="12" sm="6">
                    <v-text-field v-model="chartTitle" label="Chart Title" :color="'#c42111'" />
                </v-col>

                <v-col cols="12">
                    <v-text-field v-model="chartSubtitle" label="Chart Subtitle" :color="'#c42111'" />
                </v-col>

                <v-col cols="12" sm="6">
                    <v-text-field v-model="yAxisTitle" label="Y-Axis Label" :color="'#c42111'" :disabled="multipleAxis" />
                </v-col>

                <v-col cols="12" sm="6">
                    <v-text-field v-model="xAxisTitle" label="X-Axis Label" :color="'#c42111'" />
                </v-col>

                <!-- NETWORK ELEMENTS -->
                <v-col cols="12" v-if="$props.transpose">
                    <v-autocomplete eager autocomplete="off" :items="networkElementsList" v-model="selectedNetworkElements" multiple label="Network Elements" color="#c42111" :search-input.sync="networkElementSearch" @blur="networkElementSearch = ''" :rules="[() => !!selectedNetworkElements.length || 'This field is required']">
                        <template v-slot:prepend-item>
                            <v-list-item ripple @click="toggleSelectAllNetworkElements">
                                <v-list-item-action>
                                    <v-icon :color="selectedNetworkElements.length > 0 ? '#168A14' : ''">{{ networkElementIcon }}</v-icon>
                                </v-list-item-action>
                                <v-list-item-content>
                                    <v-list-item-title v-if="!networkElementSearch">Select All Network Elements</v-list-item-title>
                                    <v-list-item-title v-if="networkElementSearch">Select Searched Network Elements</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider class="mt-2"></v-divider>
                        </template>

                        <template v-slot:selection="{ item, index }">
                            <span v-if="index === 0">{{ item }}</span>
                            <span v-if="index === 1" class="grey--text caption"> (+{{ selectedNetworkElements.length - 1 }} others)</span>
                        </template>

                        <template v-slot:append-item>
                            <v-divider class="mb-2"></v-divider>
                            <v-list-item disabled>
                                <v-list-item-avatar color="grey lighten-3">
                                <v-icon color="grey darken">mdi-table-row</v-icon>
                                </v-list-item-avatar>

                                <v-list-item-content v-if="allNetworkElementsSelected">
                                    <v-list-item-title>All network elements selected!</v-list-item-title>
                                </v-list-item-content>
                                <v-list-item-content v-else-if="someNetworkElementsSelected">
                                    <v-list-item-title>Selected Column Count</v-list-item-title>
                                    <v-list-item-subtitle>{{ selectedNetworkElements.length }}</v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-content v-else>
                                    <v-list-item-title>
                                        Select network elements above
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        click on a network element to select for charting
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </template>
                    </v-autocomplete>
                </v-col>

                <!-- LEGEND CONFIG -->
                <v-col cols="12" sm="6" v-if="$props.transpose">
                    <v-autocomplete autocomplete="off" :items="legendValues" v-model="selectedLegendValues" multiple label="Network Element Tooltip/Legend Label" color="#c42111" :rules="[() => !!selectedLegendValues.length || 'This field is required']" />
                </v-col>
                <v-col cols="12" sm="6" v-if="$props.transpose">
                    <v-switch class="mr-8" style="display: inline-block;" v-model="hideLegend" label="Hide Legend" color="#c42111" />
                    <v-switch style="display: inline-block;" v-model="hideMetricsLegend" label="Hide selected metrics in legend" color="#c42111" :disabled="hideMetricsLegendDisabled" />
                </v-col>

                <!-- METRICS -->
                <v-col cols="12" sm="6" v-if="$props.transpose">
                    <v-autocomplete autocomplete="off" chips :items="metricsList" v-model="selectedMetrics" multiple label="Metrics" color="#c42111" :rules="[() => !!selectedMetrics.length || 'This field is required']" />
                </v-col>

                <!-- ROWS (only used for non-transposed datasets) -->
                <v-col cols="12" sm="6" v-if="!$props.transpose">
                    <v-autocomplete autocomplete="off" :items="rowsList" item-text="row_name" return-object v-model="selectedRows" multiple label="Rows" color="#c42111" :rules="[() => !!selectedRows.length || 'This field is required']" />
                </v-col>
                
                <!-- COLUMNS -->
                <v-col cols="12" sm="6">
                    <v-select v-model="selectedColumns" ref="cols" :items="columnList" :item-text="'name'" label="Columns" multiple return-object :color="'#c42111'" :rules="[() => !!selectedColumns.length || 'This field is required']">
                        <template v-slot:prepend-item>
                            <v-list-item ripple @click="toggleSelectAllColumns">
                                <v-list-item-action>
                                    <v-icon :color="selectedColumns.length > 0 ? '#168A14' : ''">{{ columnIcon }}</v-icon>
                                </v-list-item-action>
                                <v-list-item-content>
                                    <v-list-item-title>Select All Columns</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider class="mt-2"></v-divider>
                        </template>

                        <template v-slot:selection="{ item, index }">
                            <span v-if="index === 0">{{ item.name }}</span>
                            <span v-if="index === 1" class="grey--text caption"> (+{{ selectedColumns.length - 1 }} others)</span>
                        </template>

                        <template v-slot:append-item>
                            <v-divider class="mb-2"></v-divider>
                            <v-list-item disabled>
                                <v-list-item-avatar color="grey lighten-3">
                                <v-icon color="grey darken">mdi-table-column</v-icon>
                                </v-list-item-avatar>

                                <v-list-item-content v-if="allColumnsSelected">
                                    <v-list-item-title>All columns selected!</v-list-item-title>
                                </v-list-item-content>
                                <v-list-item-content v-else-if="someColumnsSelected">
                                    <v-list-item-title>Selected Column Count</v-list-item-title>
                                    <v-list-item-subtitle>{{ selectedColumns.length }}</v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-content v-else>
                                    <v-list-item-title>
                                        Select columns above
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        click on a column to select for charting
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </template>
                    </v-select>
                </v-col>

                <v-col cols="12" sm="6">
                    <v-text-field ref="lowThresh" v-model="lowThreshold" label="Low Threshold" color="#c42111" type="number" :disabled="multipleAxis" :rules="[validation.validNumber(lowThreshold), validation.lowHighValid(lowThreshold, highThreshold)]" />
                </v-col>
                <v-col cols="12" sm="6">
                    <v-select v-model="lowThresholdColor" return-object :items="colorOptions" label="Low Threshold Color" color="#c42111" type="number" :disabled="multipleAxis">
                        <template v-slot:selection="{item}">
                            <v-icon class="mr-1 color-selected" :color="item.value">mdi-checkbox-blank-circle</v-icon>
                            <span v-text="item.text" />
                        </template>
                        <template v-slot:item="{item}">
                            <v-icon class="mr-3 color-option" :color="item.value">mdi-checkbox-blank-circle</v-icon>
                            <span v-text="item.text" />
                        </template>
                    </v-select>
                </v-col>

                <v-col cols="12" sm="6">
                    <v-text-field ref="highThresh" v-model="highThreshold" label="High Threshold" color="#c42111" type="number" :disabled="multipleAxis" :rules="[validation.validNumber(lowThreshold), validation.lowHighValid(lowThreshold, highThreshold)]" />
                </v-col>
                <v-col cols="12" sm="6">
                    <v-select v-model="highThresholdColor" return-object :items="colorOptions" label="High Threshold Color" color="#c42111" type="number" :disabled="multipleAxis">
                        <template v-slot:selection="{item}">
                            <v-icon class="mr-1 color-selected" :color="item.value">mdi-checkbox-blank-circle</v-icon>
                            <span v-text="item.text" />
                        </template>
                        <template v-slot:item="{item}">
                            <v-icon class="mr-3 color-option" :color="item.value">mdi-checkbox-blank-circle</v-icon>
                            <span v-text="item.text" />
                        </template>
                    </v-select>
                </v-col>

                <v-col cols="12">
                    <v-switch style="width: 160px;" v-model="multipleAxis" color="#c42111" label="Multiple Y-Axis" @change="setMultipleAxisDefaultValues" />
                </v-col>

                <div class="multiple-axis-container" style="width: 100%;" v-if="multipleAxis">
                    <v-divider class="mb-2" />

                        <template v-for="(axis, index) in axisList">
                            <v-row dense align="center">
                                <v-col cols="auto">
                                    <span class="ml-5 mr-3 multi-axis-label">{{ `Y-Axis ${index + 1}` }}</span>
                                </v-col>
                                <v-col cols="4" lg="4">
                                    <v-autocomplete autocomplete="off" multiple label="Axis Metrics" color="#c42111" v-model="axis.metrics" :items="(selectedMetrics.map(m => {return {name: m}}))" item-text="name" item-value="name" :item-disabled="(value) => multipleAxisRowDisabledCheck(index, value)" :rules="[(() => !!axis.metrics.length || 'This field is required'), validation.allMetrics(selectedMetrics.length, totalAxisMetricsLength)]"></v-autocomplete>
                                </v-col>
                                <v-col cols="2" lg="1">
                                    <v-select v-model="axis.type" :items="chartTypes" label="Chart Type" color="#c42111" />
                                </v-col>
                                <v-col cols="2" lg="1">
                                    <v-text-field label="Axis Units" color="#c42111" v-model="axis.units" />
                                </v-col>
                                <v-col cols="3" lg="2">
                                    <v-text-field label="Axis Label" color="#c42111" v-model="axis.label" :rules="[() => !!axis.label.length || 'This field is required']" />
                                </v-col>
                                <v-col cols="auto">
                                    <v-switch class="mt-6" label="Right Side" color="#c42111" v-model="axis.right" />
                                </v-col>
                                <v-col cols="auto">
                                    <v-btn large icon @click="axisList.splice(index, 1)" class="mt-0" color="#ff5252"><v-icon large>mdi-delete-forever</v-icon></v-btn>
                                </v-col>
                            </v-row>
                        </template>

                        <v-btn class="ml-5" color="#c42111" @click="axisList.push({metrics: [], type: selectedChartType, units: '', label: '', right: false})"><v-icon class="mr-2">mdi-plus-circle</v-icon>Axis</v-btn>


                    <v-divider class="mt-6 mb-8" />
                </div>

            </v-row>
            </v-form>

            <v-btn class="build-button" @click="buildChart"><v-icon left>mdi-play</v-icon>Build Chart</v-btn>
        </v-window-item>


        <v-window-item :key="1">
            <div class="threshold-exceeded" title="High Threshold Exceeded" v-if="highThresExceeded">
                <v-icon :color="highThresholdColor ? highThresholdColor.value : '#FF0000'" large>mdi-alert-circle</v-icon>
            </div>
            <div class="threshold-exceeded" :class="{'mr-9': (lowThresExceeded && highThresExceeded)}" title="Low Threshold Exceeded" v-if="lowThresExceeded">
                <v-icon :color="lowThresholdColor ? lowThresholdColor.value : '#FF0000'" large>mdi-alert-circle</v-icon>
            </div>

            <div id="report-chart-container" ref="chartContainer">
                <highcharts :options="chartOptions" v-if="showChart"></highcharts>
                <v-menu v-model="showColorContextMenu" :position-x="colorContextMenuX" :position-y="colorContextMenuY" absolute offset-y light :close-on-content-click="false">
                    <v-list>
                        <v-list-item>
                            <v-color-picker dot-size="25" swatches-max-height="200" v-model="selectedCustomColor" mode="hexa"></v-color-picker>
                        </v-list-item>
                        <v-list-item>
                            <v-btn color="primary" width="100%" @click="applyCustomColor">Set</v-btn>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>
        </v-window-item>

    </v-window>

    <v-dialog v-model="showManageChartQuickloads" max-width="80%" width="600px" persistent v-if="showManageChartQuickloads">
        <manage-chart-quickloads :chartDetails="chartDetails" :reportChartQuickloads="reportChartQuickloads" @cancel="showManageChartQuickloads = false" @save="saveChartQuickloads" @apply="applyChartQuickload"></manage-chart-quickloads>
    </v-dialog>

    </div>
</template>

<script>
import ManageChartQuickloads from '@/components/Reports/ManageChartQuickloads';
export default {
    name: 'ReportChart',
    components: {
        ManageChartQuickloads
    },
    props: ['reportData', 'reportName', 'runReportParams', 'transpose', 'datetimeColumn', 'chartPage', 'runReportHumanText', 'refreshChart', 'viewChart'],
    data: () => ({
        formValid: true,
        showChart: false,
        chartOptions: null,
        chartTitle: "",
        chartSubtitle: "",
        chartTypes: ['Area', 'Bar', 'Column', 'Line'],
        selectedChartType: 'Line',
        xAxisTitle: "",
        yAxisTitle: "",
        selectedRows: [],
        rowsList: [],
        selectedLegendValues: [],
        legendValues: [],
        selectedColumns: [],
        columnList: [],
        metricsList: [],
        selectedMetrics: [],
        hideMetricsLegend: false,
        hideLegend: false,
        networkElementsList: [],
        selectedNetworkElements: [],
        colorOptions: [
            {text: 'Red', value: '#FF0000'},
            {text: 'Pink', value: '#FFC0CB'},
            {text: 'Orange', value: '#FFA500'},
            {text: 'Yellow', value: '#FFFF00'},
            {text: 'Violet', value: '#EE82EE'},
            {text: 'Purple', value: '#800080'},
            {text: 'Green', value: '#008000'},
            {text: 'Light Green', value: '#90EE90'},
            {text: 'Teal', value: '#008080'},
            {text: 'Aqua', value: '#00FFFF'},
            {text: 'Blue', value: '#0000FF'},
            {text: 'Brown', value: '#A52A2A'},
            {text: 'Gray', value: '#808080'},
            {text: 'Dark Gray', value: '#2F4F4F'},
        ],
        lowThreshold: null,
        lowThresholdColor: null,
        highThreshold: null,
        highThresholdColor: null,
        lowThresExceeded: false,
        highThresExceeded: false,


        multipleAxis: false,
        axisList: [],
        validation: {
            allMetrics: (selectedMetricsLength, totalAxisMetricsLength) => (selectedMetricsLength == totalAxisMetricsLength) || 'Not all metrics accounted for',
            validNumber: (value) => (!isNaN(Number(value)) || `Value must be valid number`),
            lowHighValid: (low, high) => (!low || !high || Number(low) < Number(high) || 'Low Threshold must be below High Threshold'),
        },
        allColsDefaultSelectedAlready: false,
        showManageChartQuickloads: false,
        reportChartQuickloads: [],

        selectedHighchartsSeriesIndex: null,
        showColorContextMenu: false,
        colorContextMenuX: null,
        colorContextMenuY: null,
        selectedCustomColor: null,
        chartColors: null,
        networkElementSearch: ''
    }),

    computed: {
        allNetworkElementsSelected () {
            return this.selectedNetworkElements.length === this.networkElementsList.length;
        },
        someNetworkElementsSelected () {
            return this.selectedNetworkElements.length > 0 && !this.allNetworkElementsSelected;
        },
        networkElementIcon () {
            if (this.allNetworkElementsSelected) return 'mdi-close-box';
            if (this.someNetworkElementsSelected) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        },

        allColumnsSelected () {
            return this.selectedColumns.length === this.columnList.length;
        },
        someColumnsSelected () {
            return this.selectedColumns.length > 0 && !this.allColumnsSelected;
        },
        columnIcon () {
            if (this.allColumnsSelected) return 'mdi-close-box';
            if (this.someColumnsSelected) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        },

        totalAxisMetricsLength () {
            let total = 0;
            this.axisList.forEach(a => {
                total += a.metrics.length;
            });
            return total;
        },

        chartHeight() {
            if(this.$route.name == 'reports') {
                return window.innerHeight - 230;
            }
            else if(this.$route.name == 'reporthistory') {
                return window.innerHeight - 150;
            }
            return 600;
        },

        hideMetricsLegendDisabled() {
            if(this.hideLegend || this.selectedMetrics.length != 1) {
                if(this.hideMetricsLegend) {
                    this.hideMetricsLegend = false;
                }
                return true;
            }
            return false;
        },

        chartDetails() {
            return {
                chartTitle: this.chartTitle,
                chartSubtitle: this.chartSubtitle,
                selectedChartType: this.selectedChartType,
                xAxisTitle: this.xAxisTitle,
                yAxisTitle: this.yAxisTitle,
                selectedMetrics: this.selectedMetrics,
                hideMetricsLegend: this.hideMetricsLegend,
                hideLegend: this.hideLegend,
                lowThreshold: this.lowThreshold,
                lowThresholdColor: this.lowThresholdColor,
                highThreshold: this.highThreshold,
                highThresholdColor: this.highThresholdColor,
                multipleAxis: this.multipleAxis,
                axisList: this.axisList
            };
        }
    },

    methods: {
        buildResourceLists: function() {
            this.chartTitle = this.$props.reportName;
            if(this.$props.runReportHumanText) {
                this.chartSubtitle = Array.isArray(this.$props.runReportHumanText) ? this.$props.runReportHumanText.join('; ') : this.$props.runReportHumanText;
            }
            if(!this.$props.transpose) {
                this.rowsList = this.$props.reportData.data;
                this.columnList = this.$props.reportData.cols.filter(col => {
                    return (col.type.toLowerCase() == "f" || col.type.toLowerCase() == "i");
                });
            }
            else {
                //Transpose Build. Little more complicated
                let columns = [];
                let columnObjs = [];
                let timeColumn = this.$props.datetimeColumn;
                let formattedRowNames = [];

                // LEGENG OPTIONS - build this list.
                this.legendValues = this.$props.reportData.cols.filter(c => c.graph_row == true).map(c => {return c.name});
                if(!this.selectedLegendValues.length) {
                    // If this is the first time theyre editing the chart then we want to have all values selected by default.
                    this.selectedLegendValues = this.legendValues;
                }


                // COLUMNS - dates
                // This builds a list of all the different datetimes. 1 value for every row of data.
                for(let row of this.$props.reportData.data) {
                    columns.push(row[timeColumn]);
                }
                // Chrono sort dates.
                columns.sort((a,b) => {
                    return new Date(a) - new Date(b);
                });
                // Remove the duplicates?? Yep. Theres a duplicate time col for each metric. Eg so 4 different metrics means 4x the cols
                columns = [...new Set(columns)];
                columns.forEach(col => {
                    columnObjs.push({name: col});
                });
                this.columnList = columnObjs;


                // METRICS - get a list of columns that are numeric types because those are the ones we can graph
                let numericColumns = this.$props.reportData.cols.filter(col => {
                    return (col.type.toLowerCase() == "f" || col.type.toLowerCase() == "i");
                });
                this.metricsList = numericColumns.map(c => c.name);


                // NETWORK ELEMENTS - Get list of unique row names
                let rows = this.$props.reportData.data;
                for(let row of rows) {
                    let rowNameArr = row.row_name.split(' ').filter(text => {
                        return (!(TELSA.moment(text, 'YYYY-MM-DD', true).isValid() 
                            || TELSA.moment(text, 'YYYY-MM-DD HH:mm:ss', true).isValid() // impossible?
                            || TELSA.moment(text, 'HH:mm:ss', true).isValid()
                            || TELSA.moment(text, 'YYYY-MM-DD HH:mm:ssZ', true).isValid() // impossible?
                            || TELSA.moment(text, 'HH:mm:ssZ', true).isValid()
                            || TELSA.moment(text, 'YYYY-WW', true).isValid()));
                    });
                    row.rowNameFormatted = rowNameArr.join(' ');
                    formattedRowNames.push(row.rowNameFormatted);
                }
                formattedRowNames = [...new Set(formattedRowNames)];
                this.networkElementsList = formattedRowNames;
            }
        },

        toggleSelectAllNetworkElements () {
            let searchText = this.networkElementSearch;
            if(searchText) {
                let filteredItems = this.networkElementsList.filter(x => {
                    return x.toLocaleLowerCase().indexOf(searchText.toLocaleLowerCase()) > -1;
                });
                for(let f of filteredItems) {
                    if(!this.selectedNetworkElements.includes(f)) {
                        this.selectedNetworkElements.push(f);
                    }
                }

            }
            else {
                if(this.allNetworkElementsSelected) {
                    this.selectedNetworkElements = [];
                } else {
                    this.selectedNetworkElements = this.networkElementsList.slice();
                }
            }
        },

        toggleSelectAllColumns () {
            if (this.allColumnsSelected) {
                this.selectedColumns = [];
            } else {
                this.selectedColumns = this.columnList.slice();
            }
        },

        buildChart: function() {
            if(!this.$refs.chartForm.validate()) {
                return;
            }

            let selectedColumnNames = [];
            let seriesData = [];
            let yAxisList = [];
            this.lowThresExceeded = false;
            this.highThresExceeded = false;

            if(!this.$props.transpose) {
                // Non-Tanspose pretty simple
                for(let column of this.selectedColumns) {
                    selectedColumnNames.push(column.name);
                }

                for(let selectedRow of this.selectedRows) {
                    let data = [];
                    for(let selectedCol of this.selectedColumns) {
                        data.push(Number(String(selectedRow[selectedCol.name]).replace(/,/g, '')));
                    }
                    seriesData.push({
                        name: selectedRow.row_name,
                        data: data
                    });
                }
            }
            else {
                // Transpose data. little more complicated
                for(let column of this.selectedColumns) {
                    selectedColumnNames.push(column.name);
                }

                let reportDataRows = this.reportData.data;
                let timeColumn = this.$props.datetimeColumn;
                let dataFound = false;
                for(let [metricIndex, metric] of this.selectedMetrics.entries()) {
                    let color = this.$store.state.batchConfig.webapp_settings.reports.metricCommonColor ? this.chartColors[metricIndex] : null;
                    for(let networkElement of this.selectedNetworkElements) {
                        let data = [];
                        let rowZero = null;
                        for(let selectedCol of this.selectedColumns) {
                            dataFound = false;
                            for(let dataRow of reportDataRows) {
                                if(dataRow.rowNameFormatted == networkElement && dataRow[timeColumn] == selectedCol.name) {
                                    rowZero = rowZero || dataRow;
                                    data.push(Number(dataRow[metric].replace(/,/g, '')));
                                    dataFound = true;
                                    break;
                                }
                            }
                            if(!dataFound) {
                                data.push(null);
                            }
                        }

                        let seriesDataLegendName = "";
                        for(let legendCol of this.selectedLegendValues) {
                            seriesDataLegendName += `${rowZero[legendCol]} `;
                        }
                        seriesData.push({
                            //name: `${metric} - ${seriesDataLegendName}`,
                            name: `${this.hideMetricsLegend ? "" : (metric + ' - ')}${seriesDataLegendName}`,
                            metric: metric,
                            networkElement: networkElement,
                            data: data,
                            showInLegend: !this.hideLegend,
                            color: color
                        });
                    }
                }

                if(this.multipleAxis) {
                    // create the yAxis's
                    let axisColor = null;
                    for(let [index, axis] of this.axisList.entries()) {
                        axisColor = null;

                        for(let [seriesIndex, series] of seriesData.entries()) {
                            if(axis.metrics.includes(series.metric)) {
                                series.yAxis = index;
                                series.type = axis.type.toLowerCase();
                                //axisColor = (axisColor ? '#2F2F2F' : this.chartColors[seriesIndex]);
                                axisColor = (axisColor ? '#2F2F2F' : this.$store.state.batchConfig.webapp_settings.reports.chartColors[seriesIndex]);
                            }
                        }

                        yAxisList.push({
                            title: {
                                text: axis.label,
                                style: {
                                    color: axisColor
                                }
                            },
                            labels: {
                                format: `{value} ${axis.units}`,
                                style: {
                                    color: axisColor
                                }
                            },
                            opposite: axis.right
                        });
                    }
                }
                
                let weeklySummaryReport = (
                    this.$props.runReportHumanText && 
                    (
                        (Array.isArray(this.$props.runReportHumanText) && this.$props.runReportHumanText.some(x => x.toLowerCase() == "weekly summary")) 
                        || (typeof this.$props.runReportHumanText == "string" && this.$props.runReportHumanText.toLowerCase().includes("weekly summary"))
                    ));
                for(let seriesDataItem of seriesData) {
                    // This is formatting the dates to unix times so highcharts can build the custom datetime ranges on the x-axis
                    for (let [i, col] of selectedColumnNames.entries()) {
                        let yVal = seriesDataItem.data[i];
                        let xVal
                        
                        // We have to subract the tzOffset(convert minutes to seconds) because converting a time to unix() always makes it UTC time.
                        //let xVal = ((TELSA.moment(col).unix() - (new Date().getTimezoneOffset() * 60)) * 1000);
                        //let xVal = (TELSA.moment(col).unix() * 1000);
                        if(TELSA.moment(col, 'YYYY-MM-DD HH:mm:ssZ', true).isValid()) {
                            //console.log('DOING WEIRD TZ STUFF!!!');
                            xVal = (TELSA.moment(col).unix() * 1000);
                            xVal += ((3600 * Number(col.substr(-3))) * 1000);
                        }
                        else {
                            // It looks like the timezone parsing failed so we need to get it the old-fashioned way. We also need to account for 
                            // "week number" format. moment interprets XXXX-XX as YYYY-MM but it could be YYYY-WW. So check the report params for "Weekly Summary"
                            if(weeklySummaryReport && /^([0-9][0-9][0-9][0-9]-[0-9][0-9])$/.test(col)) {
                                xVal = ((TELSA.moment(col, 'YYYY-WW').unix() - (new Date().getTimezoneOffset() * 60)) * 1000);
                            }
                            else {
                                xVal = ((TELSA.moment(col).unix() - (new Date().getTimezoneOffset() * 60)) * 1000);
                            }
                        }
                        seriesDataItem.data[i] = [xVal, yVal];
                    }
                    if(!this.multipleAxis) {
                        let lastVal = seriesDataItem.data[seriesDataItem.data.length - 1];
                        this.lowThresExceeded = (this.lowThresExceeded || (!!this.lowThreshold && lastVal[1] < Number(this.lowThreshold)));
                        this.highThresExceeded = (this.highThresExceeded || (!!this.highThreshold && lastVal[1] > Number(this.highThreshold)));
                    }
                }
            }

            let self = this;
            this.chartOptions = {
                colors: this.chartColors,
                title: {
                    text: this.chartTitle,
                    style: {
                        color: "#2F2F2F",
                        fontWeight: "bold"
                    }
                },
                subtitle: {
                    text: this.chartSubtitle,
                    useHTML: true,
                    widthAdjust: -200,
                    style: {
                        color: "#2F2F2F"
                    }
                },
                chart: {
                    type: this.selectedChartType.toLowerCase(),
                    height: this.chartHeight,
                    backgroundColor: "#ffffff",
                    color: "#2F2F2F",
                    borderRadius: "5px",
                    inverted: false,
                    zoomType: 'x',
                    styledMode: false
                },


                exporting: {
                    sourceWidth: 800,
                    sourceHeight: 500,
                },


                credits: {
                    enabled: false
                },
                legend: {
                    itemStyle: {
                        color: "#2F2F2F"
                    },
                    enabled: true
                },
                xAxis: {
                    // Columns
                    categories: (this.$props.transpose ? null : selectedColumnNames),
                    title: {
                        text: this.xAxisTitle
                    },
                    labels: {
                        style: {
                            color: "#2F2F2F"
                        }
                    },
                    type: 'datetime'
                },
                yAxis: this.multipleAxis ? yAxisList : {
                    title: {
                        text: this.yAxisTitle
                    },
                    labels: {
                        style: {
                            color: "#2F2F2F"
                        }
                    },
                    plotLines: [
                        {
                            color: this.lowThresholdColor ? this.lowThresholdColor.value : '#FF0000',
                            width: 4,
                            value: this.lowThreshold ? this.lowThreshold : null,
                        },
                        {
                            color: this.highThresholdColor ? this.highThresholdColor.value : '#FF0000',
                            width: 4,
                            value: this.highThreshold ? this.highThreshold : null,
                        }
                    ]
                },
                series: seriesData,
                plotOptions: {
                    series: {
                        point: {
                            events: {
                                mouseOver: function(event) {
                                    if(!self.multipleAxis) {
                                        return;
                                    }
                                    let point = event.target;
                                    let series = point.series;
                                    let highlightColor = '#c42111';

                                    if (point.graphic) {
                                        point.graphic.attr({
                                            fill: highlightColor
                                        });
                                    }

                                    series.chart.series.forEach(function(s) {
                                        if (s.isHighlighted) {
                                            self.highlightSeries(s, s.color);
                                            s.isHighlighted = false;
                                        }
                                    });

                                    self.highlightSeries(series, highlightColor);
                                    series.isHighlighted = true;
                                },
                                mouseOut: function(event) {
                                    if(!self.multipleAxis) {
                                        return;
                                    }
                                    let point = event.target;
                                    let series = point.series;
                                    self.highlightSeries(series, series.color);
                                }
                            }
                        }
                    }
                },
                events: {
                    render: (this.$store.state.batchConfig.webapp_settings.reports.allowRightClickEditChartColors ? this.addRightClickListener() : null) // If they don't have this setting enabled then don't allow this functionality
                }
            };
            this.$emit('update:chartPage', 1);
            this.showChart = true;
        },

        highlightSeries: function(series, color) {
            for (let key in series.yAxis.ticks) {
                series.yAxis.ticks[key].label.css({
                    fill: color,
                    fontWeight: (color == '#c42111' ? 'bold' : 'inherit'),
                    fontSize: (color == '#c42111' ? '12px' : '11px'),
                });
            }

            series.yAxis.axisTitle.css({
                fill: color,
                fontWeight: (color == '#c42111' ? 'bold' : 'inherit'),
                fontSize: (color == '#c42111' ? '12px' : '11px'),
            });
        },

        resetValues: function() {
            this.chartOptions = null;
            this.chartTitle = "";
            this.chartSubtitle = "";
            this.chartTypes = [];
            this.selectedChartType = null;
            this.xAxisTitle = "";
            this.yAxisTitle = "";
            this.selectedColumns = [];
            this.columnList = [];
            this.transDataRows = [];
        },

        setMultipleAxisDefaultValues: function(value) {
            return;
            if(!value) {
                return;
            }
            this.axisList = [];
            for(let row of this.selectedRows) {
                this.axisList.push({
                    rows: [row.row_name],
                    type: this.selectedChartType,
                    units: '',
                    label: row.row_name,
                    right: false
                });
            }
        },

        multipleAxisRowDisabledCheck: function(axisIndex, networkElement) {
            for(let [index, axis] of this.axisList.entries()) {
                if(axisIndex != index && axis.metrics.includes(networkElement.name)) {
                    return true;
                }
            }
            return false;
        },

        getReportChartQuickloads: function() {
            let route = `${TELSA.apiHost}report/chart/quickloads/list`;
            return this.$http.post(route, {args: {report: this.$props.reportName}}).then(response => {
                let results = response.body;
                this.reportChartQuickloads = results;
            });
        },

        showManageChartQuickloadsClick: function() {
            this.showManageChartQuickloads = true;
        },

        saveChartQuickloads: function(quickloads) {
            let route = `${TELSA.apiHost}report/chart/quickloads/save`;
            return this.$http.post(route, {args: {report: this.$props.reportName, quickloads: quickloads}}).then(response => {
                let results = response.body;
                this.reportChartQuickloads = quickloads;
                this.showManageChartQuickloads = false;

            });
        },

        applyChartQuickload: function(quickload) {
            this.chartTitle = quickload.chartDetails.chartTitle;
            this.chartSubtitle = quickload.chartDetails.chartSubtitle;
            this.selectedChartType = quickload.chartDetails.selectedChartType;
            this.xAxisTitle = quickload.chartDetails.xAxisTitle;
            this.yAxisTitle = quickload.chartDetails.yAxisTitle;
            this.selectedMetrics = quickload.chartDetails.selectedMetrics;
            this.hideMetricsLegend = quickload.chartDetails.hideMetricsLegend;
            this.hideLegend = quickload.chartDetails.hideLegend;
            this.lowThreshold = quickload.chartDetails.lowThreshold;
            this.lowThresholdColor = quickload.chartDetails.lowThresholdColor;
            this.highThreshold = quickload.chartDetails.highThreshold;
            this.highThresholdColor = quickload.chartDetails.highThresholdColor;
            this.multipleAxis = quickload.chartDetails.multipleAxis;
            this.axisList = quickload.chartDetails.axisList;

            this.showManageChartQuickloads = false;
        },


        chartSeriesRightClickHandler: function(event) {
            event.preventDefault(); // Prevent the default context menu
            // Ignore this if they didn't click on a series
            if(!event.target.classList.contains('highcharts-point')) {
                return;
            }
            let match = false;
            for (let className of event.target.classList) {
                match = className.match(/highcharts-color-(\d+)/);
                if(match) {
                    break;
                }
            }
            if(!match) {
                return;
            }
            this.colorContextMenuX = event.clientX;
            this.colorContextMenuY = event.clientY;
            this.selectedHighchartsSeriesIndex = match[1];
            this.selectedCustomColor = this.chartColors[this.selectedHighchartsSeriesIndex];
            this.showColorContextMenu = true;
        },

        addRightClickListener: function() {
            let self = this;
            let setupHandler = function() {
                document.getElementById('report-chart-container').removeEventListener('contextmenu', self.chartSeriesRightClickHandler);
                document.getElementById('report-chart-container').addEventListener('contextmenu', self.chartSeriesRightClickHandler);
            }
            this.$nextTick(() => {
                setTimeout(setupHandler, 2000);
            });
        },

        applyCustomColor: function() {
            let hexColor = this.selectedCustomColor;
            this.chartColors[this.selectedHighchartsSeriesIndex] = hexColor;
            this.showColorContextMenu = false;
            this.colorContextMenuX = null;
            this.colorContextMenuY = null;
            this.selectedHighchartsSeriesIndex = null;
            this.selectedCustomColor = null;
            this.showChart = false;
            this.$nextTick(() => {
                this.showChart = true;
            });
        }
    },

    watch: {
        viewChart: function(newVal, oldVal) {
            if(newVal === true) {
                this.buildResourceLists();
            }
            if(!this.allColsDefaultSelectedAlready && this.$store.state.batchConfig.webapp_settings.reports && this.$store.state.batchConfig.webapp_settings.reports.defaultChartAllColumns) {
                if(!this.allColumnsSelected) {
                    this.toggleSelectAllColumns();
                    this.allColsDefaultSelectedAlready = true;
                }
            }
        },

        refreshChart: function(newVal, oldVal) {
            if(this.showChart) {
                this.buildResourceLists();
                if(this.$props.chartPage === 1) {
                    if(!this.allColumnsSelected) {
                        this.toggleSelectAllColumns();
                    }
                    this.buildChart();
                }
            }
        }

    },

    mounted() {
        this.buildResourceLists();
        this.getReportChartQuickloads();
    },

    created() {
        this.chartColors = JSON.parse(JSON.stringify(this.$store.state.batchConfig.webapp_settings.reports.chartColors));
    },
    destroyed() {
        if(document.getElementById('report-chart-container')) {
            document.getElementById('report-chart-container').removeEventListener('contextmenu', this.chartSeriesRightClickHandler);
        }
    }

  
};
</script>

<style scoped>

    .build-button {
        color: #ffffff;
        background-color: #c42111 !important;
    }

    .edit-button {
        color: #ffffff;
        background-color: #c42111 !important;
        position: absolute;
        bottom: 0;
        z-index: 999;
    }

    .multi-axis-label {
        display: inline-block;
        font-weight: bold;
        font-size: 18px;
    }

    .color-option {
        text-shadow: -1px 0 #fff, 0 1px #fff, 1px 0 #fff, 0 -1px #fff;
    }

    .color-selected {
        text-shadow: -1px 0 #000, 0 1px #000, 1px 0 #000, 0 -1px #000;
    }

</style>

<style>

    .highcharts-title, .highcharts-subtitle {
        white-space: nowrap !important;
        text-overflow: ellipsis !important;
        overflow: hidden !important
    }

    .threshold-exceeded {
        position: absolute;
        text-align: center;
        top: 5px;
        right: 40px;
        animation: blinker 1s linear infinite;
        z-index: 2;
    }
    @keyframes blinker {
        50% {
            opacity: 0;
        }
    }

</style>
